import { GridsterItem } from "angular-gridster2";
import { MultiLangField } from "src/app/shared/customization/multilang-input/multi-language";

export type pieType = 'pie' | 'doughnut' | 'gauge' | 'polarArea' | 'termometer';
export type barType = 'horizontal' | 'vertical' | 'horizontalStacked' | 'verticalStacked';
export type lineType = 'line';

export interface Widget extends GridsterItem {
  id: any;
  widgetType: WidgetType;
  widgetTypeCategory?: TableWidgetPrebuiltType | KpiWidgetPrebuiltType 
    | ChartWidgetTypeCategory | WidgetFilterType | WidgetMachineDataType | null;
  config: WidgetConfig;
  isCompositeWidget?: boolean;
  data?: any;
  instance?: any; // Needed for ECharts
}

export interface WidgetConfig {
  generic: WidgetGenericConfig;
  specific?: WidgetFilterConfig | WidgetKpiConfig | WidgetDividerConfig 
    | WidgetChartConfig | WidgetMachineConfig | WidgetTableConfig | WidgetImageConfig | any;
}

export interface WidgetGenericConfig {
  title?: MultiLangField;
  showTitle?: boolean;
  description?: string;
  canDownloadChart?: boolean;
  canExport?: boolean;
  canExpand?: boolean;
  canNavigate?: boolean;
  routeTo?: string;
  removable?: boolean;
}

export interface WidgetFilterConfig {
  label?: string;
  selectType?: 'single' | 'multiple';
  selectDataType?: WidgetFilterSelectDataType;
  defaultValue?: any;
}

export interface WidgetKpiConfig {
  signalType?: any | any[];
  orientation: 'vertical' | 'horizontal';
  icon?: string | null;
}

export interface WidgetChartConfig {
  signalType?: SignalCatalog[];
  type: pieType | barType | lineType;
  showLegend: boolean;
  legendPosition?: 'top' | 'bottom' | 'left' | 'right';
  legendOrient?: 'horizontal' | 'vertical';
  lineStyle?: 'line' | 'points' | 'line+points';
  doubleYAxis?: boolean;
  termometer?: TermometerOptions;
}

export interface WidgetDividerConfig {
  orientation: 'horizontal' | 'vertical'; 
  color?: string;
  width?: string;
}

export interface WidgetMachineConfig {
  signalType?: SignalCatalog;
}

export interface WidgetTableConfig {
  type?: string;
  columns?: WidgetTableColumn[];
  showPagination?: boolean;
  showFilter?: boolean;
}

export interface WidgetImageConfig {
  image?: any; // Base64 string or URL
  hasCustomImage?: boolean; // Flag to indicate if user uploaded custom image
  altText?: string;
}

export interface KpiData {
  result_value: string;
  result_avg_value: string;
  result_vin: string;
  result_signal: string;
  signal_unit: string;
  description?: string; // Optional description for the KPI
  name?: string; // Label risolta dall'expectedKpi corrispondente (vedi extractExpectedKpi)
  i18nLabel?: { [key: string]: string };
  isTimeValue?: boolean; // true se il segnale è un valore hour/time (formattato come hh:mm)
}

export class WidgetTableColumn {
  name: string = '';
  label: string = '';
  type: any;
  sortable: boolean = false;
  hidden: boolean = false;

  constructor(name: string, type: string) {
    this.name = name;
    this.label = name;
    this.type = type;
  }
}

export class TermometerOptions {
  min: number = 0;
  max: number = 100;
  intervals: { limit: number, color: string }[] = [
    { limit: 50, color: '#FFA500' },
    { limit: 100, color: '#FF0000' }
  ];
}

export interface SignalApiResponse {
  avg_value: number;
  granularity: string;
  max_value: number;
  min_value: number;
  tstamp: string;
  vin: string;
  value?: number;
}

export interface SignalsApiRequest {
  customerId: string, 
  signals: string[] | { key: string, scope: string }[], // Needed key/scope pair for custom KPIs , 
  datesRange: any[],
  fromDate: any,
  toDate: any,
  machineIds: string[],
  aggregateData?: boolean
}

export class TableApiRequest {
  page: number = 0;
  pageSize: number = 10;
  direction: string | null = null;
  orderBy: string | null = null;
  filter: string | null = null;
  tableType: string | null = null;
}

/** Ciò che viene effettivamente persistito in widget.config.specific.signalType[]. */
export interface SignalRef {
  uid: string;
  name: string;
  scope: string;
  /** Override utente (icona per-KPI), NON è un campo di catalogo. */
  icon?: string | null;
}

export interface SignalCatalog extends SignalRef {
  // Needed for custom KPIs to identify the signal in the API command
  // and handle portal customization
  i18nLabel?: { [key: string]: string };
  visible?: boolean;
  apiKey?: {
    key: string,
    scope: string
  };

  label: string,
  unit: string,
  type: string,
  /** Popolati dal rehydrate: il segnale non è più selezionabile / non esiste più. */
  disabledInCatalog?: boolean;
  deletedFromCatalog?: boolean;
}

/**
 * Un segnale è renderizzabile solo se il catalogo lo consente: disabilitato o eliminato significa
 * che il dato non va mostrato all'utente finale (il view-container è un risultato, non una
 * configurazione). I flag sono popolati dal rehydrate (SignalRehydrationService).
 * Assenti (es. dashboard non ancora reidratata) => segnale renderizzabile.
 */
export function isRenderableSignal(s: SignalCatalog | null | undefined): boolean {
  return !!s && !s.disabledInCatalog && !s.deletedFromCatalog;
}

export enum WidgetType {
  TEXT = 'text',
  KPI = 'kpi',
  MACHINE_DATA = 'machine_data',
  SIGNAL = 'signal',
  PIE = 'pie',
  TABLE = 'table',
  MAP = 'map',
  FILTER = 'filter',
  DIVIDER = 'divider',
  IMAGE = 'image',
}

export enum ChartWidgetTypeCategory {
  PIE = 'pie',
  BAR = 'bar',
  LINE = 'line',
}

export enum TableWidgetPrebuiltType {
  AttivitaVeicoli,
  ScadenzeManutenzioni
}

export enum KpiWidgetPrebuiltType {
  STANDARD = 'standard',
  ALERT = 'alert',
  MAINTENANCE = 'maintenance',
  VEHICLES_RECAP = 'machine_recap',
  MACHINE_ALERT = 'machineAlert',
}

export enum WidgetFilterType {
  SELECT = 'select',
  DATE = 'date',
}

export enum WidgetFilterSelectDataType {
  VEHICLES = 'vehicles',
}

export enum WidgetMachineDataType {
  OVERVIEW = 'overview',
}

export enum RouterTo {

}

export enum DateRange {
  TenMinutes = '10min',
  ThirtyMinutes = '30min',
  OneHour = '1hour',
  SixHours = '6hour',
  TwelveHours = '12hour',
  TwentyFourHours = '24hour',
  SevenDays = '7days',
  FourteenDays = '14days',
  ThirtyDays = '30days',
  SixtyDays = '60days',
  NinetyDays = '90days',
  SixMonths = '6months',
  OneYear = '1year'
}

export enum SignalDataType {
  DOUBLE = 'double',
  STRING = 'string',
  STRING_ = 'String',
  DATETIME = 'datetime',
  BOOLEAN = 'boolean',
  INT = 'int',
  TEXT = 'text',
  NUMBER = 'Number',
}

export enum TableDataType {
  TEXT = 'text',
  NUMERIC = 'numeric',
  INTEGER = 'integer',
  SMALLINT = 'smallint',
  BOOLEAN = 'boolean',
  DOUBLE_PRECISION = 'double precision',
  TIMESTAMP_WITH_TIME_ZONE = 'timestamp with time zone'
}

export interface VehicleDataTs {
  ccid: string;
  curr_config_received: string;
  curr_firmw: string;
  curr_firmw_id: number;
  device_creation_date: string;
  device_customer_id: number;
  device_customer_name: string;
  device_id: number;
  device_model_id: number;
  device_model_name: string;
  device_phone_number: string;
  device_vehicle_start: string;
  imei: string;
  is_default_fmw: boolean;
  next_firmw: string;
  next_firmw_id: number;
  serial_number: string;
  vehicle_customer_id: number;
  vehicle_customer_name: string;
  vehicle_customer_start: string;
  vehicle_id: number;
  vehicle_vin: string;
}